import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';

const ConversionFunnelChart = () => {
  const data = [
    { 
      stage: 'Applied', 
      count: 1250, 
      percentage: 100, 
      dropoff: 0,
      color: '#3b82f6',
      avgDays: 0
    },
    { 
      stage: 'Screened', 
      count: 875, 
      percentage: 70, 
      dropoff: 30,
      color: '#6366f1',
      avgDays: 3
    },
    { 
      stage: 'Phone Interview', 
      count: 525, 
      percentage: 42, 
      dropoff: 28,
      color: '#8b5cf6',
      avgDays: 7
    },
    { 
      stage: 'Technical Round', 
      count: 315, 
      percentage: 25.2, 
      dropoff: 16.8,
      color: '#a855f7',
      avgDays: 12
    },
    { 
      stage: 'Final Interview', 
      count: 189, 
      percentage: 15.1, 
      dropoff: 10.1,
      color: '#c084fc',
      avgDays: 18
    },
    { 
      stage: 'Offer Extended', 
      count: 94, 
      percentage: 7.5, 
      dropoff: 7.6,
      color: '#e879f9',
      avgDays: 21
    },
    { 
      stage: 'Hired', 
      count: 78, 
      percentage: 6.2, 
      dropoff: 1.3,
      color: '#10b981',
      avgDays: 23
    }
  ];

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload?.length) {
      const data = payload?.[0]?.payload;
      return (
        <div className="bg-white/95 backdrop-blur-sm border border-slate-200 rounded-lg p-4 shadow-lg min-w-48">
          <h4 className="font-semibold text-slate-900 mb-3">{data?.stage}</h4>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-slate-600">Candidates:</span>
              <span className="font-semibold text-slate-900">{data?.count}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-600">Conversion:</span>
              <span className="font-semibold text-slate-900">{data?.percentage}%</span>
            </div>
            {data?.dropoff > 0 && (
              <div className="flex justify-between">
                <span className="text-slate-600">Drop-off:</span>
                <span className="font-semibold text-red-600">{data?.dropoff}%</span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-slate-600">Avg. Days:</span>
              <span className="font-semibold text-slate-900">{data?.avgDays}</span>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  const totalConversion = ((data?.[data?.length - 1]?.count / data?.[0]?.count) * 100);
  const biggestDropoff = Math.max(...data?.map(d => d?.dropoff));
  const bottleneckStage = data?.find(d => d?.dropoff === biggestDropoff);

  return (
    <div className="space-y-6">
      {/* Header with Key Insights */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold text-slate-900 mb-1">Hiring Funnel Analysis</h3>
          <p className="text-sm text-slate-500">Stage-by-stage conversion tracking</p>
        </div>
        
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="text-center p-3 bg-emerald-50 rounded-lg">
            <div className="text-xl font-bold text-emerald-600">{totalConversion?.toFixed(1)}%</div>
            <div className="text-xs text-emerald-700">Overall Conversion</div>
          </div>
          <div className="text-center p-3 bg-red-50 rounded-lg">
            <div className="text-xl font-bold text-red-600">{biggestDropoff}%</div>
            <div className="text-xs text-red-700">Biggest Bottleneck</div>
          </div>
        </div>
      </div>

      {/* Funnel Chart */}
      <div className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart 
            data={data} 
            layout="horizontal"
            margin={{ top: 20, right: 60, left: 20, bottom: 20 }}
          >
            <CartesianGrid 
              strokeDasharray="3 3" 
              stroke="#e2e8f0"
              horizontal={false}
            />
            
            <XAxis 
              type="number"
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 12, fill: '#64748b' }}
            />
            
            <YAxis 
              type="category"
              dataKey="stage"
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 12, fill: '#64748b' }}
              width={120}
            />
            
            <Tooltip content={<CustomTooltip />} />
            
            <Bar dataKey="count" radius={[0, 6, 6, 0]}>
              {data?.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry?.color} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Stage Breakdown Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {data?.slice(1)?.map((stage, index) => (
          <div 
            key={stage?.stage} 
            className={`p-3 rounded-lg border-l-4 ${
              stage?.dropoff > 20 ? 'bg-red-50 border-red-400' :
              stage?.dropoff > 10 ? 'bg-amber-50 border-amber-400': 'bg-emerald-50 border-emerald-400'
            }`}
          >
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-slate-700">{stage?.stage}</span>
              <div className="flex items-center gap-2">
                <span className="text-xs px-2 py-1 bg-white rounded text-slate-600">
                  Day {stage?.avgDays}
                </span>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-lg font-bold text-slate-900">{stage?.count}</span>
              <div className="text-right">
                <div className="text-sm font-semibold text-slate-900">{stage?.percentage}%</div>
                {stage?.dropoff > 0 && (
                  <div className={`text-xs ${
                    stage?.dropoff > 20 ? 'text-red-600' :
                    stage?.dropoff > 10 ? 'text-amber-600': 'text-slate-500'
                  }`}>
                    -{stage?.dropoff}% drop
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Optimization Recommendations */}
      <div className="p-4 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg border border-indigo-200">
        <div className="flex items-start gap-3">
          <div className="p-2 bg-indigo-100 rounded-lg mt-0.5">
            <svg className="w-4 h-4 text-indigo-600" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="flex-1">
            <h4 className="font-semibold text-indigo-900 mb-2">Optimization Insights</h4>
            <div className="text-sm text-indigo-800 space-y-1">
              <p>• <strong>{bottleneckStage?.stage}</strong> shows highest drop-off rate ({biggestDropoff}%) - consider process improvements</p>
              <p>• Average conversion rate industry benchmark: 8-12% (Current: {totalConversion?.toFixed(1)}%)</p>
              <p>• Technical round optimization could improve overall funnel by ~15%</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConversionFunnelChart;